import { useState, useEffect, useCallback, useRef } from 'react'
import { AD_SLOTS } from './BannerAd'

declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}

interface InterstitialAdProps {
  isVisible: boolean
  onClose: () => void
}

const CLOSE_DELAY_SECONDS = 5
const AD_CHECK_DELAY = 2000

function InterstitialAd({ isVisible, onClose }: InterstitialAdProps) {
  const [countdown, setCountdown] = useState(CLOSE_DELAY_SECONDS)
  const [canClose, setCanClose] = useState(false)
  const [adFailed, setAdFailed] = useState(false)
  const adRef = useRef<HTMLDivElement>(null)
  const insRef = useRef<HTMLModElement>(null)
  const isAdPushed = useRef(false)

  // Reset state when ad becomes visible
  useEffect(() => {
    if (isVisible) {
      setCountdown(CLOSE_DELAY_SECONDS)
      setCanClose(false)
      setAdFailed(false)
      isAdPushed.current = false
    }
  }, [isVisible])

  // Countdown timer
  useEffect(() => {
    if (!isVisible || adFailed) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanClose(true)
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isVisible, adFailed])

  // Initialize ad when visible
  useEffect(() => {
    if (!isVisible || isAdPushed.current) return

    if (typeof window !== 'undefined') {
      window.adsbygoogle = window.adsbygoogle || []
    }

    // Small delay to ensure DOM is ready
    const pushTimeout = setTimeout(() => {
      if (adRef.current && !isAdPushed.current) {
        try {
          window.adsbygoogle.push({})
          isAdPushed.current = true
        } catch (err) {
          console.error('AdSense interstitial error:', err)
          setAdFailed(true)
          onClose()
        }
      }
    }, 100)

    // Check if ad loaded after delay
    const checkTimeout = setTimeout(() => {
      if (insRef.current) {
        const adStatus = insRef.current.getAttribute('data-ad-status')
        const hasChildren = insRef.current.children.length > 0
        const hasHeight = insRef.current.offsetHeight > 10
        const isUnfilled = adStatus === 'unfilled'
        const isFilled = adStatus === 'filled'

        // Auto-close if ad didn't load
        if (isUnfilled || (!isFilled && !hasChildren && !hasHeight)) {
          setAdFailed(true)
          onClose()
        }
      }
    }, AD_CHECK_DELAY)

    return () => {
      clearTimeout(pushTimeout)
      clearTimeout(checkTimeout)
    }
  }, [isVisible, onClose])

  // Handle escape key
  useEffect(() => {
    if (!isVisible || !canClose) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isVisible, canClose, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isVisible && !adFailed) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isVisible, adFailed])

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (canClose && e.target === e.currentTarget) {
      onClose()
    }
  }, [canClose, onClose])

  if (!isVisible || adFailed) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-[90%] mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
          <span className="text-sm text-neutral-dark/70">Advertisement</span>
          {canClose ? (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-metro-blue rounded-full hover:bg-metro-blue-dark transition-colors"
            >
              Close
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            <span className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-dark/50 bg-gray-200 rounded-full">
              <span>Close in {countdown}s</span>
              <div className="w-4 h-4 border-2 border-neutral-dark/30 border-t-metro-blue rounded-full animate-spin" />
            </span>
          )}
        </div>

        {/* Ad Content */}
        <div ref={adRef} className="p-4 min-h-[300px] flex items-center justify-center">
          <ins
            ref={insRef}
            className="adsbygoogle"
            style={{ display: 'block', width: '300px', height: '250px' }}
            data-ad-client="ca-pub-3940256099942544"
            data-ad-slot={AD_SLOTS.INTERSTITIAL}
            data-ad-format="rectangle"
          />
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t text-center">
          <p className="text-xs text-neutral-dark/50">
            Support Indian Metro Web by viewing this ad
          </p>
        </div>
      </div>
    </div>
  )
}

export default InterstitialAd
